import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';

import useDelayedBusy from '../../hooks/useDelayedBusy';
import Skeleton from './Skeleton';

/**
 * The six states a surface that fetches can be in, and only ever one at a
 * time (docs/platform-standards.md §14).
 */
export const DATA_STATE = Object.freeze({
	LOADING: 'loading-initial',
	REFRESHING: 'refreshing',
	SAVING: 'saving',
	READY: 'ready',
	EMPTY: 'empty',
	ERROR: 'error',
});

const STATES = Object.values(DATA_STATE);

/**
 * Turn the flags a surface already has into its one state.
 *
 *   deriveDataState({ loading, saving, error, hasData, isEmpty })
 *
 * `hasData` is "the first request for this view has succeeded": a second
 * visit, a filter change or a revalidation is `refreshing`, never a blank
 * skeleton. `isEmpty` is "it succeeded and there is nothing". Precedence:
 * a first load wins over everything (so `empty` can never show while the
 * request is in flight); then a failure; then a refresh; then a save; then
 * empty; then ready.
 */
export function deriveDataState({ loading = false, saving = false, error = null, hasData = false, isEmpty = false } = {}) {
	if (loading && !hasData) return DATA_STATE.LOADING;
	if (error) return DATA_STATE.ERROR;
	if (loading) return DATA_STATE.REFRESHING;
	if (saving) return DATA_STATE.SAVING;
	if (isEmpty) return DATA_STATE.EMPTY;
	return DATA_STATE.READY;
}

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Renders a surface in exactly one of the six data states, so a screen
 * cannot forget one.
 *
 *   <DataState
 *     state={deriveDataState({ loading, error, hasData, isEmpty })}
 *     hasData={hasData}
 *     isEmpty={isEmpty}
 *     skeleton={<Skeleton variant='list-row' count={6} />}
 *     empty={<EmptyState title='No items yet' />}
 *     error={error}
 *     onRetry={reload}
 *     label='items'
 *   >
 *     <ItemList items={items} />
 *   </DataState>
 *
 * - loading-initial: nothing for the first LOADING_DELAY_MS, then `skeleton`;
 *   "Still loading" after LOADING_HINT_MS, in a slot reserved up front; an
 *   error with Retry after LOADING_TIMEOUT_MS. Once shown, the skeleton stays
 *   LOADING_MIN_VISIBLE_MS even if the data arrives sooner, and nothing else
 *   (empty included) is drawn under it meanwhile.
 * - refreshing: the previous children (or the `empty` node, when `isEmpty`)
 *   stay put with a quiet bar along the top. Never a skeleton.
 * - saving: the children, unchanged. The control that was pressed shows its
 *   own pending state; the page does not dim.
 * - ready: the children. empty: `empty`.
 * - error: what failed and a Retry that calls `onRetry`. With `hasData` the
 *   previous children stay and the failure sits above them.
 *
 * The region carries `aria-busy` while loading-initial or refreshing. A
 * polite live region says "<label> loaded" once, when a first load finishes
 * (not on every refresh), and says a failure every time. Focus is never moved.
 *
 * Timing comes from useDelayedBusy and so from src/config/loadingTiming.js.
 * `timing` overrides it for the gallery and tests only.
 */
export default function DataState({
	state,
	isEmpty = false,
	hasData = false,
	skeleton = null,
	empty = null,
	error = null,
	onRetry,
	label = 'this',
	hintText = 'Still loading',
	timing,
	className = '',
	children,
	...rest
}) {
	let current = state === 'loading' ? DATA_STATE.LOADING : state;
	if (!STATES.includes(current)) {
		if (process.env.NODE_ENV !== 'production') console.error(`DataState: unknown state "${state}"`);
		current = DATA_STATE.LOADING;
	}
	// A first load over data we already hold is a refresh, whatever the caller said.
	if (current === DATA_STATE.LOADING && hasData) current = DATA_STATE.REFRESHING;

	const busy = current === DATA_STATE.LOADING || current === DATA_STATE.REFRESHING;
	const { visible, hint, timedOut } = useDelayedBusy(busy, timing);

	// Which kind of wait last showed a placeholder, so the minimum-visible hold
	// keeps the right one on screen after the wait ends.
	const lastBusy = useRef(current);
	if (busy) lastBusy.current = current;
	const holdingSkeleton = !busy && visible && lastBusy.current === DATA_STATE.LOADING;

	// What is on screen, as distinct from what the caller asked for.
	let shown = current;
	if (holdingSkeleton) shown = DATA_STATE.LOADING;
	else if (busy && timedOut) shown = DATA_STATE.ERROR;

	const stale = hasData || current === DATA_STATE.REFRESHING;
	const kept = isEmpty ? empty : children;
	const failure = `Unable to load ${label}.`;
	const detail = timedOut && busy ? 'It took too long.' : typeof error === 'string' ? error : null;
	const failureText = detail ? `${failure} ${detail}` : failure;

	// The announcement follows what is on screen, once per transition.
	const [message, setMessage] = useState('');
	const previous = useRef(null);
	useEffect(() => {
		const before = previous.current;
		previous.current = shown;
		if (shown === DATA_STATE.ERROR) setMessage(failureText);
		else if (shown === DATA_STATE.LOADING || shown === DATA_STATE.REFRESHING) setMessage('');
		else if (before === DATA_STATE.LOADING) setMessage(`${capitalise(label)} loaded`);
	}, [shown, failureText, label]);

	const retry = onRetry ? (
		<Button className='data-state__retry' onClick={onRetry}>
			Retry
		</Button>
	) : null;

	let body;
	if (shown === DATA_STATE.LOADING) {
		body = visible ? (
			<>
				{skeleton || <Skeleton variant='block' />}
				<p className='data-state__hint'>{hint ? hintText : ''}</p>
			</>
		) : null;
	} else if (shown === DATA_STATE.ERROR) {
		body = stale ? (
			<>
				<div className='data-state__notice'>
					<span className='data-state__notice-text'>{failureText}</span>
					{retry}
				</div>
				{kept}
			</>
		) : (
			<div className='data-state__error'>
				<p className='data-state__error-title'>{failure}</p>
				{detail ? <p className='data-state__error-detail'>{detail}</p> : null}
				{retry}
			</div>
		);
	} else if (shown === DATA_STATE.EMPTY) {
		body = empty || <p className='data-state__empty'>Nothing here yet.</p>;
	} else if (shown === DATA_STATE.REFRESHING) {
		body = kept;
	} else {
		body = children;
	}

	// The refresh cue is a bar along the top edge, out of the layout flow, so
	// it cannot move anything. It follows the same delay and hold as a skeleton.
	const cue = visible && lastBusy.current === DATA_STATE.REFRESHING && !timedOut;

	return (
		<div
			className={`data-state${className ? ` ${className}` : ''}`}
			data-state={shown}
			aria-busy={shown === DATA_STATE.LOADING || shown === DATA_STATE.REFRESHING ? 'true' : undefined}
			{...rest}
		>
			{cue ? <span className='data-state__cue' aria-hidden='true' /> : null}
			{body}
			<div className='loading-sr-only' role='status' aria-live='polite' aria-atomic='true'>
				{message}
			</div>
		</div>
	);
}
